/**
 * Progress & Wins Widget
 * 
 * Track member progress and celebrate wins publicly.
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Trophy, ArrowRight, Loader2, Sparkles, Star, TrendingUp,
  Calendar, BookOpen, MessageSquare, Plus, Heart, PartyPopper
} from 'lucide-react';

const API_URL = window.location.origin;

const CELEBRATION_REACTIONS = ["❤️", "🎉", "🤝🏾", "💡", "🤔", "👏🏾", "💪🏾", "🙌🏾", "🙏🏾", "💎", "🔥", "✨"];

const ProgressWinsWidget = ({ className = '' }) => {
  const [progress, setProgress] = useState(null);
  const [wins, setWins] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [activeTab, setActiveTab] = useState('progress');
  
  // Share win form
  const [winTitle, setWinTitle] = useState('');
  const [winDescription, setWinDescription] = useState('');
  const [winCategory, setWinCategory] = useState('personal_growth');

  useEffect(() => {
    fetchProgress();
    fetchWins();
    fetchCategories();
  }, []);

  const fetchProgress = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/progress/mine`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProgress(response.data);
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWins = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/wins/feed?limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWins(response.data.wins || []);
    } catch (error) {
      console.error('Failed to fetch wins:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wins/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleShareWin = async () => {
    if (!winTitle.trim() || !winDescription.trim()) {
      toast.error('Please fill in both title and description');
      return;
    }

    setSharing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/wins/share`,
        {
          title: winTitle,
          description: winDescription,
          category: winCategory,
          is_public: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message || 'Your win has been shared!');
      setShowShareDialog(false);
      setWinTitle('');
      setWinDescription('');
      setWinCategory('personal_growth');
      
      // Refresh wins and progress
      fetchWins();
      fetchProgress();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share win');
    } finally {
      setSharing(false);
    }
  };

  const handleReact = async (winId, reaction) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/wins/${winId}/react`,
        { reaction },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Celebration sent!');
      fetchWins();
    } catch (error) {
      if (error.response?.data?.detail?.includes("own win")) {
        toast.info("You can't react to your own win");
      } else {
        toast.error(error.response?.data?.detail || 'Failed to react');
      }
    }
  };

  if (loading) {
    return (
      <Card className={`card-base ${className}`} data-testid="progress-wins-widget">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-terracotta" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={`card-base ${className}`} data-testid="progress-wins-widget">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-500" />
              Your Journey & Wins
            </CardTitle>
            <Button
              size="sm"
              onClick={() => setShowShareDialog(true)}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="share-win-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              Share a Win
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="progress">My Progress</TabsTrigger>
              <TabsTrigger value="wins">Sister Wins</TabsTrigger>
            </TabsList>

            <TabsContent value="progress" className="space-y-4">
              {progress && (
                <>
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="p-3 bg-amber-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-amber-600">
                        {progress.stats?.diamond_points || 0}
                      </p>
                      <p className="text-xs text-gray-600">Diamonds</p>
                    </div>
                    <div className="p-3 bg-forest/5 rounded-lg text-center">
                      <p className="text-2xl font-bold text-forest">
                        {progress.stats?.days_as_member || 0}
                      </p>
                      <p className="text-xs text-gray-600">Days</p>
                    </div>
                    <div className="p-3 bg-purple-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-purple-600">
                        {progress.stats?.wins_shared || 0}
                      </p>
                      <p className="text-xs text-gray-600">Wins Shared</p>
                    </div>
                    <div className="p-3 bg-terracotta/5 rounded-lg text-center">
                      <p className="text-2xl font-bold text-terracotta">
                        {progress.stats?.journal_entries || 0}
                      </p>
                      <p className="text-xs text-gray-600">Journal Entries</p>
                    </div>
                  </div>

                  {/* Pathway Progress */}
                  {progress.pathway?.initialized && (
                    <div className="p-4 bg-gradient-to-r from-terracotta/5 to-amber-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-forest">30-Day Activation</span>
                        <Badge className="bg-terracotta/10 text-terracotta text-xs">
                          Week {progress.pathway.current_week}
                        </Badge>
                      </div>
                      <Progress value={progress.pathway.progress_percent} className="h-2" />
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-gray-500">
                          {progress.stats?.tasks_completed || 0} tasks completed
                        </span>
                        <span className="text-xs text-gray-500">
                          {Math.round(progress.pathway.progress_percent)}%
                        </span>
                      </div>
                    </div>
                  )}

                  {/* YUMMY Declaration */}
                  {progress.yummy_declaration?.completed && (
                    <div className="p-3 bg-purple-50 border border-purple-100 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Sparkles className="h-4 w-4 text-purple-600" />
                        <span className="text-sm font-medium text-purple-700">Your YUMMY Declaration</span>
                      </div>
                      <p className="text-sm text-gray-600 italic line-clamp-2">
                        "{progress.yummy_declaration.declaration}"
                      </p>
                    </div>
                  )}

                  {/* Next Milestone */}
                  {progress.milestones?.next && (
                    <div className="p-3 bg-amber-50 rounded-lg flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Star className="h-5 w-5 text-amber-500" />
                        <div>
                          <p className="text-sm font-medium text-forest">
                            {progress.milestones.next.label}
                          </p>
                          <p className="text-xs text-gray-500">
                            {progress.milestones.next.days_until} days away
                          </p>
                        </div>
                      </div>
                      <TrendingUp className="h-5 w-5 text-amber-500" />
                    </div>
                  )}
                </>
              )}
            </TabsContent>

            <TabsContent value="wins" className="space-y-3">
              {wins.length === 0 ? (
                <div className="text-center py-6">
                  <PartyPopper className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">No wins shared yet</p>
                  <p className="text-gray-400 text-xs">Be the first to celebrate!</p>
                </div>
              ) : (
                wins.map(win => (
                  <div key={win.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={win.user_image} />
                        <AvatarFallback className="bg-amber-100 text-amber-600 text-xs">
                          {win.user_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-forest text-sm">
                            {win.user_name?.split(' ')[0]}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {win.category_emoji} {win.category_label}
                          </Badge>
                        </div>
                        <p className="font-medium text-sm text-forest mt-1">
                          {win.title}
                        </p>
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {win.description}
                        </p>
                        
                        {/* Reactions */}
                        <div className="flex items-center gap-2 mt-2">
                          <div className="flex gap-1">
                            {CELEBRATION_REACTIONS.slice(0, 5).map(reaction => (
                              <button
                                key={reaction}
                                onClick={() => handleReact(win.id, reaction)}
                                className="hover:scale-125 transition-transform text-lg"
                                title="Celebrate!"
                              >
                                {reaction}
                              </button>
                            ))}
                          </div>
                          {win.total_reactions > 0 && (
                            <span className="text-xs text-gray-500">
                              {win.total_reactions} celebrations
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Share Win Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-500" />
              Share Your Win
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Celebrate your wins with your sisters! Every win matters, big or small.
            </p>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-forest">Category</label>
              <Select value={winCategory} onValueChange={setWinCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.emoji} {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-forest">Title</label>
              <Input
                placeholder="What's your win?"
                value={winTitle}
                onChange={(e) => setWinTitle(e.target.value)}
                maxLength={100}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-forest">Description</label>
              <Textarea
                placeholder="Tell us more about this win..."
                value={winDescription}
                onChange={(e) => setWinDescription(e.target.value)}
                rows={3}
                maxLength={500}
              />
            </div>

            <div className="p-3 bg-amber-50 rounded-lg">
              <div className="flex items-center gap-2 text-amber-700 text-sm font-medium">
                <Sparkles className="h-4 w-4" />
                You'll earn 15 diamonds for sharing!
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShareDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleShareWin}
              disabled={sharing || !winTitle.trim() || !winDescription.trim()}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {sharing ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sharing...</>
              ) : (
                <><PartyPopper className="h-4 w-4 mr-2" />Share Win</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProgressWinsWidget;
